import { Injectable } from "@angular/core";
import { map } from "rxjs/operators";

import { Antenna } from "../models/antenna";
import { AppState } from "../app-state";

import { TestbedRxdbGraphQlCollectionService } from "./testbed-rxdb/testbed-rxdb-graphql-collection.service";
import { TestbedRxdbState } from "./testbed-rxdb/testbed-rxdb-state";
import { AuthenticationService } from "./authentication.service";
import {
  AntennaDoc,
  antennaPullQueryBuilder,
  antennaSchema,
} from "../schema/antenna-schema";

export function isNumericFloor(floor: string): boolean {
  return /^\d+$/.test(floor);
}

export interface GetAntennasParams {
  buildingId: string;
  floorName?: string;
  floorNumber?: number;
  stage: string;
}

export interface BuildingFloorCombination {
  buildingId: string;
  floorName?: string;
  floorNumber?: number;
}

export interface GetAntennasMultiParams {
  buildingFloors: BuildingFloorCombination[];
  stage: string;
}

@Injectable({ providedIn: "root" })
export class AntennaService extends TestbedRxdbGraphQlCollectionService<
  Antenna,
  AntennaDoc
> {
  constructor(
    appState: AppState,
    authService: AuthenticationService,
    rxdbState: TestbedRxdbState
  ) {
    super("antenna", antennaSchema, appState, rxdbState, authService);
    this.syncConfig = {
      push: false,
      pull: true,
      intervalMs: this.appState.settings.get(
        "rxdb.collections.antenna.intervalMs",
        0
      ),
      retry: true,
      bGraphQL: true,
      query: antennaPullQueryBuilder(),
      graphQLSubscription: `subscription{
                antennaUpdated{
                    antennas{
                        id
                        updatedAt
                        deleted
                    }
                }
            }`,
    };
  }

  getAntennas$(params: GetAntennasParams) {
    console.log("[AntennaService] getAntennas$() called with params:", params);

    if (!params.buildingId) {
      throw new Error("buildingId is required");
    }

    if (!params.stage) {
      throw new Error("stage is required");
    }

    if (!params.floorName && params.floorNumber == null) {
      throw new Error("Either floorName or floorNumber must be provided");
    }

    // Use simple RxDB query for building_id and filter the rest with TypeScript
    return this.rxCollection
      .find()
      .where("building_id")
      .eq(params.buildingId)
      .$.pipe(
        map((antennas) =>
          antennas.filter((antennaDoc) => {
            const antenna = antennaDoc.toJSON();

            // Stage matching: exact match or null/empty stage
            const stageMatches =
              antenna.stage === params.stage ||
              antenna.stage === "" ||
              antenna.stage == null;

            if (!stageMatches) {
              return false;
            }

            // Floor matching: prefer floorNumber over floorName
            let floorMatches = false;

            if (params.floorNumber != null) {
              floorMatches = antenna.floor_number === params.floorNumber;
            }

            if (!floorMatches && params.floorName) {
              // Match by floor name
              floorMatches = antenna.floor_name === params.floorName;

              // If floorName is numeric and no explicit floorNumber, also try floor_number
              if (
                !floorMatches &&
                params.floorNumber == null &&
                isNumericFloor(params.floorName)
              ) {
                floorMatches =
                  antenna.floor_number === parseInt(params.floorName);
              }
            }

            return floorMatches;
          })
        )
      );
  }

  getAntennasMulti$(params: GetAntennasMultiParams) {
    console.log("[AntennaService] getAntennasMulti$() called with params:", params);

    if (!params.buildingFloors || params.buildingFloors.length === 0) {
      throw new Error("buildingFloors array is required and must not be empty");
    }

    if (!params.stage) {
      throw new Error("stage is required");
    }

    // Extract unique building IDs
    const buildingIds = [...new Set(params.buildingFloors.map(bf => bf.buildingId))];

    // Use RxDB query to get antennas from all relevant buildings
    return this.rxCollection
      .find()
      .where("building_id")
      .in(buildingIds)
      .$.pipe(
        map((antennas) =>
          antennas.filter((antennaDoc) => {
            const antenna = antennaDoc.toJSON();

            // Stage matching: exact match or null/empty stage
            const stageMatches =
              antenna.stage === params.stage ||
              antenna.stage === "" ||
              antenna.stage == null;

            if (!stageMatches) {
              return false;
            }

            // Check if this antenna matches any of the building/floor combinations
            return params.buildingFloors.some(buildingFloor => {
              // Building must match
              if (antenna.building_id !== buildingFloor.buildingId) {
                return false;
              }

              // Floor matching: prefer floorNumber over floorName
              let floorMatches = false;

              if (buildingFloor.floorNumber != null) {
                floorMatches = antenna.floor_number === buildingFloor.floorNumber;
              }

              if (!floorMatches && buildingFloor.floorName) {
                // Match by floor name
                floorMatches = antenna.floor_name === buildingFloor.floorName;

                // If floorName is numeric and no explicit floorNumber, also try floor_number
                if (
                  !floorMatches &&
                  buildingFloor.floorNumber == null &&
                  isNumericFloor(buildingFloor.floorName)
                ) {
                  floorMatches =
                    antenna.floor_number === parseInt(buildingFloor.floorName);
                }
              }

              return floorMatches;
            });
          })
        )
      );
  }
}
